import {
  CircleAlert,
  CircleCheck,
  Inbox,
  Info,
  LoaderCircle,
  Minus,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ChangeEvent, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { digitsOnly, groupDigits } from '../../lib/format.js';

/** The standard content panel — `surface` plus padding. */
export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`surface p-5 ${className}`}>{children}</section>;
}

/**
 * THE DESCRIPTION SITS UNDER THE ROW, not beside the action. It used to share
 * the title's flex item, so whatever width the action took — a "Lihat semua"
 * link is ~100px — came out of the description's column for its whole height.
 * On a phone a panel has ~260px to give, and "Sinyal bukan tuduhan — baca
 * buktinya" wrapped into four lines beside empty space under the link. The row
 * now holds only the title and the action; the sentence gets the full width.
 */
export function PanelHeader({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <header className="mb-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="min-w-0 text-lg">{title}</h2>
        {action}
      </div>
      {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
    </header>
  );
}

/** Uppercase kicker — the brand's signature text device (design-guidelines §2.2). */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <p className="eyebrow">{children}</p>;
}

/**
 * The change against the previous window of equal length.
 *
 * `goodWhen` exists because direction is not sentiment: more visits is good,
 * more flagged visits is not, and a tile that paints every rise green would be
 * congratulating a supervisor on a fraud spike.
 */
export interface StatDelta {
  /** Already formatted, sign included — e.g. "+12,4%". */
  value: string;
  direction: 'up' | 'down' | 'flat';
  goodWhen: 'up' | 'down';
  /** What it is compared against, e.g. "vs 30 hari sebelumnya". */
  label: string;
}

/**
 * A KPI tile. `value` is a string so the caller has already decided how to
 * render it — several of these come off `avg()` as numeric strings and rounding
 * is the caller's business, not the tile's.
 *
 * Three things about the styling are deliberate and easy to undo by accident:
 *
 * The icon is `text-steel`, never `primary`. §1.2: `primary #249CD8` is 3.08:1
 * on white and is decoration only. It is also NOT tinted by `tone` — that would
 * make hue a second status channel, and §1.3 requires status to survive without
 * colour at all.
 *
 * The number keeps proportional figures. `tabular-nums` is for columns that are
 * scanned down; at 2rem it makes "121" look loose beside "888".
 *
 * A linked tile gets `hover:bg-tint` and no lift. §6 dropped the translate-on-
 * hover from the brand site on purpose, and a KPI row that jumps under the
 * cursor is the worst place to reintroduce it.
 */
export function Stat({
  label,
  value,
  hint,
  tone = 'default',
  icon: Icon,
  delta,
  to,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'warn' | 'success';
  icon?: LucideIcon;
  delta?: StatDelta;
  /** Makes the whole tile a link to the screen that explains the number. */
  to?: string;
  /** The exact figure, when `value` has been abbreviated for width. */
  title?: string;
}) {
  const valueTone = {
    default: 'text-navy',
    warn: 'text-warn',
    success: 'text-success-strong',
  }[tone];

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="eyebrow">{label}</p>
        {Icon ? <Icon size={18} className="mt-px shrink-0 text-steel" aria-hidden /> : null}
      </div>
      <p
        className={`mt-2 font-display text-[2rem] font-bold leading-none tracking-[-0.02em] ${valueTone}`}
        title={title}
      >
        {value}
      </p>
      {delta ? (
        <Delta {...delta} />
      ) : hint ? (
        <p className="mt-1 text-sm text-muted">{hint}</p>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="focus-ring surface block p-4 transition-colors duration-150 hover:bg-tint"
      >
        {body}
      </Link>
    );
  }

  return <div className="surface p-4">{body}</div>;
}

function Delta({ value, direction, goodWhen, label }: StatDelta) {
  const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;
  // Colour reinforces; the glyph and the sign carry it. Legible in greyscale.
  const tone =
    direction === 'flat'
      ? 'text-muted'
      : direction === goodWhen
        ? 'text-success-strong'
        : 'text-warn';

  /*
   * TWO LINES, NOT ONE TRUNCATED ONE.
   *
   * This was `-15,4% vs 30 hari sebelumnya` on a single row with the comparison
   * `truncate`d, and the tile is never wide enough to hold it: two columns on a
   * phone leaves ~120px of text, and `lg:grid-cols-6` on the overview is barely
   * wider. So every tile rendered "vs 30 h…", which is the half of the sentence
   * that carries no information — a reader can see the percentage, what they
   * cannot see is what it is a percentage AGAINST. A tooltip does not rescue it
   * either; there is no hover on the device where it is worst.
   *
   * The comparison drops to `text-xs text-muted` because it is the same string
   * on all six tiles: said once per tile it must not compete with the figure.
   */
  return (
    <div className="mt-1.5">
      <p className={`flex items-center gap-1 text-sm ${tone}`}>
        <Icon size={14} className="shrink-0" aria-hidden />
        <span className="font-semibold">{value}</span>
      </p>
      <p className="mt-0.5 text-xs text-muted">{label}</p>
    </div>
  );
}

/**
 * Pill badge (design-guidelines §7). Always carries a word, never only a hue.
 *
 * `whitespace-nowrap` because a pill is one token: "Saat ini" broken over two
 * lines inside a rounded-full frame reads as a rendering fault, not as a label.
 * It also stops the badge shrinking when it shares a flex row with prose — a
 * flex item cannot go below its min-content width, and with the wrap forbidden
 * that width is the whole word. `PanelHeader`'s `action` slot is exactly this
 * case, and it is where the two-line pill came from.
 */
export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'success' | 'warn' | 'danger' | 'info'; children: ReactNode }) {
  const styles = {
    neutral: 'bg-panel text-muted',
    success: 'bg-success-surface text-success-strong',
    warn: 'bg-warn-surface text-warn',
    danger: 'bg-danger-surface text-danger',
    info: 'bg-tint text-primary-700',
  }[tone];
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[0.6875rem] font-bold uppercase tracking-[0.04em] ${styles}`}>
      {children}
    </span>
  );
}

/**
 * The banner.
 *
 * The icon is the point of the redesign, not decoration: §1.3 requires status to
 * survive without colour, and until now these four tones differed only by hue.
 * A red-green colourblind reader could not tell `error` from `success`.
 */
export function Banner({
  tone,
  live,
  children,
}: {
  tone: 'warn' | 'info' | 'error' | 'success';
  /**
   * This banner APPEARED in response to something the user just did — a save
   * that was rejected, a list that failed to load — and must be announced.
   *
   * Opt-in rather than derived from `tone`, because most banners in this console
   * are standing context that happens to be red or amber: the locked-period
   * notice on a filter bar, the "kode tidak dapat diubah" hint in a form. Those
   * are already in the reading order, and `role="alert"` on them would re-read
   * the same sentence on every re-render of the screen around them.
   */
  live?: boolean;
  children: ReactNode;
}) {
  const { styles, Icon } = {
    warn: { styles: 'bg-warn-surface text-warn border-warn/20', Icon: TriangleAlert },
    info: { styles: 'bg-tint text-primary-700 border-line', Icon: Info },
    error: { styles: 'bg-danger-surface text-danger border-danger/20', Icon: CircleAlert },
    success: { styles: 'bg-success-surface text-success-strong border-success/20', Icon: CircleCheck },
  }[tone] as { styles: string; Icon: LucideIcon };

  return (
    <div
      // `alert` for a failure, `status` for anything else: an assertive region
      // interrupts whatever is being read, which is right for "this did not
      // save" and rude for "12 rows exported".
      {...(live ? { role: tone === 'error' ? 'alert' : 'status' } : {})}
      className={`flex items-start gap-2.5 rounded-xl border p-3 text-sm ${styles}`}
    >
      <Icon size={18} className="mt-px shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-muted">
      <LoaderCircle size={18} className="shrink-0 animate-spin text-primary-700" aria-hidden />
      {label}
    </div>
  );
}

/**
 * The empty state.
 *
 * Its own component because "no rows" and "no rows *yet*" and "no rows matching
 * these filters" are different messages, and a table that renders nothing at all
 * reads as broken.
 */
export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <span className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-panel text-muted">
        <Icon size={22} aria-hidden />
      </span>
      <p className="font-display font-bold text-navy">{title}</p>
      {description ? <p className="max-w-sm text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * Whether the control inside the current `Field` is the one that was rejected.
 *
 * A CONTEXT RATHER THAN A PROP because `Field` takes its control as `children`
 * and cannot reach into it. The alternative was `aria-invalid` written by hand
 * beside every `error={...}` at ~60 call sites, which is the kind of pairing
 * that is right the day it is written and half-applied a month later. This way a
 * form marks a field once and the red border, the message and the announcement
 * all follow from it.
 */
const FieldInvalid = createContext(false);

export function useFieldInvalid(): boolean {
  return useContext(FieldInvalid);
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-semibold text-navy">{label}</span>
      <FieldInvalid.Provider value={error !== undefined && error !== ''}>{children}</FieldInvalid.Provider>
      {/*
        The message REPLACES the hint rather than joining it. The hint explains
        the field in the abstract ("Bersifat unik."); once the server has said
        what is actually wrong with this value, that is the more useful of the
        two and stacking both pushes the next field down the form.
      */}
      {error ? <span className="text-sm text-danger">{error}</span> : hint ? <span className="text-sm text-muted">{hint}</span> : null}
    </label>
  );
}

/**
 * The frame every text-shaped control wears — border, height, ground colour.
 *
 * Exported and split out of `CONTROL` for `Combobox`, whose frame is a DIV with
 * an input and a clear button inside it: it takes its own padding, and its own
 * focus ring, because `focus-visible` on a div that never receives focus never
 * matches. Composed straight back below, so a plain field and a combobox cannot
 * drift a border colour or a corner radius apart.
 */
export const CONTROL_SHELL =
  // `aria-invalid` is the single source of the rejected look, so the state that
  // assistive technology reads and the state a sighted user sees cannot come
  // apart — and a combobox's div frame gets the same border as a plain input
  // without either file knowing about the other.
  'h-control w-full rounded-xl border border-line bg-white text-base text-ink aria-[invalid=true]:border-danger aria-[invalid=true]:bg-danger-surface';

const CONTROL = `focus-ring ${CONTROL_SHELL} px-3 placeholder:text-muted disabled:bg-panel disabled:text-muted`;

/**
 * `aria-invalid` comes from the enclosing `Field`, and an explicit prop still
 * wins — a control can be rejected by something other than a server issue.
 */
export function Input({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  const invalid = useFieldInvalid();
  return <input aria-invalid={invalid || undefined} {...rest} className={`${CONTROL} ${className}`} />;
}

/**
 * A free-text master-data field, folded to upper case as it is typed.
 *
 * MASTER DATA IS A KEY, not prose. A code, a name and an area are matched,
 * deduplicated and grouped by, and "Aqua 600ml", "AQUA 600ML" and "aqua 600ml"
 * are three rows in a GROUP BY — one product reported as three, or one
 * distributor whose outlets land in two areas. The importer has always folded
 * case when it resolves a channel, a classification or a region code; the forms
 * did not, so whichever spelling reached the console first is the one every
 * report afterwards carried. Same failure the channel and category masters were
 * created to end, one level down.
 *
 * FOLDED IN `onChange`, not at save time, so the operator reads the value that
 * is actually being stored — and so a name that already exists looks like the
 * one it collides with before the server rejects it.
 *
 * NOT `text-transform: uppercase`, which changes the picture and not the value:
 * the lower-case string still reaches the API, and the field then lies about
 * what was saved.
 *
 * WHY THE CARET LOGIC, as in `RupiahInput`: the fold rewrites `value` on every
 * keystroke, and React puts the caret at the end of the field whenever it
 * writes a value the DOM does not already hold. Typing a name fresh hides that,
 * because the caret is already at the end — but correcting a letter mid-word
 * throws the cursor to the far end after every character. The offset is
 * recomputed by folding the text BEFORE the caret and measuring it, rather than
 * reused as-is, because upper-casing is not always length-preserving ("ß" folds
 * to "SS") and a pasted string can carry such a character.
 */
export function UppercaseInput({
  onChange,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const invalid = useFieldInvalid();
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    // Before paint, so the caret never renders in the wrong place first.
    if (caret.current !== null && ref.current) {
      ref.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  });

  const handle = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const upper = raw.toUpperCase();

    // Nothing to fold — digits, punctuation, an already-upper-case paste. Left
    // alone so the caret is not touched on the common keystroke.
    if (upper !== raw) {
      const selection = event.target.selectionStart ?? raw.length;
      caret.current = raw.slice(0, selection).toUpperCase().length;
      // Written back onto the node so the handler below, and React's own value
      // tracker, both see the folded string.
      event.target.value = upper;
    }

    onChange?.(event);
  };

  return (
    <input
      aria-invalid={invalid || undefined}
      {...rest}
      ref={ref}
      type="text"
      onChange={handle}
      className={`${CONTROL} ${rest.className ?? ''}`}
    />
  );
}

/**
 * A rupiah amount, grouped as it is typed: 1500000 shows as "1.500.000".
 *
 * `value` and `onChange` speak DIGITS, never the masked text. The separator is a
 * display concern and letting it into state is how `Number("1.500.000")` becomes
 * NaN — or, worse in `id-ID`, how a value that parses as 1.5 reaches an API
 * expecting rupiah.
 *
 * `type="text"` rather than `type="number"`, because a number input rejects the
 * separators this component's whole purpose is to insert. `inputMode="numeric"`
 * still brings up the digit keypad on the mobile fallback.
 *
 * WHY THE CARET LOGIC IS HERE
 * A naive masked input rewrites `value` on every keystroke, and React then
 * restores the caret to the end of the field. Typing a price fresh hides that,
 * because the caret is already at the end — but correcting a leading digit
 * throws the cursor to the far end after every character, which is the kind of
 * defect that gets described as "the form is broken" rather than reported. The
 * fix is to count digits before the caret and put it back after the same digit,
 * which is stable no matter how many separators the edit added or removed.
 */
export function RupiahInput({
  value,
  onChange,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** Digits only, e.g. "12500". Empty string means "no amount". */
  value: string;
  onChange: (digits: string) => void;
}) {
  const invalid = useFieldInvalid();
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);

  useLayoutEffect(() => {
    // Before paint, so the caret never renders in the wrong place first.
    if (caret.current !== null && ref.current) {
      ref.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
  });

  const handle = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const selection = event.target.selectionStart ?? raw.length;
    const digitsBeforeCaret = digitsOnly(raw.slice(0, selection)).length;

    const digits = digitsOnly(raw);
    const grouped = groupDigits(digits);

    // Walk the formatted string until the same number of digits has passed.
    let seen = 0;
    let position = grouped.length;
    for (let index = 0; index < grouped.length; index += 1) {
      if (seen === digitsBeforeCaret) {
        position = index;
        break;
      }
      if (/\d/.test(grouped[index] as string)) seen += 1;
    }
    caret.current = seen === digitsBeforeCaret ? position : grouped.length;

    onChange(digits);
  };

  return (
    <input
      aria-invalid={invalid || undefined}
      {...rest}
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={groupDigits(value)}
      onChange={handle}
      className={`${CONTROL} tabular-nums ${rest.className ?? ''}`}
    />
  );
}

/**
 * A native select.
 *
 * STILL THE RIGHT CONTROL for a bounded list — roles, statuses, page sizes,
 * regions, channels, categories. The native control is what a phone gives the
 * mobile fallback for free, and a list that cannot outgrow its dropdown does not
 * need anything more.
 *
 * NOT the right control for a list that grows: SKUs, outlets and salespeople use
 * `Combobox`, which searches server-side and states what it is not showing. The
 * hooks behind those three used to ask for one 200-row page, so row 201 could
 * not be selected at all — silently, because a short list looks complete.
 *
 * This docblock used to say a plain list of strings "does not need 40kB of
 * popover", and that was true of `@radix-ui/react-select` when it was written.
 * It is no longer an argument against `Combobox`: `Shell.tsx` already ships
 * `react-dropdown-menu`, so the popper, the dismissable layer, the portal and
 * the focus scope are in the bundle regardless, and Popover adds one small hook
 * package on top of them.
 */
export function Select({ className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const invalid = useFieldInvalid();
  return (
    <select aria-invalid={invalid || undefined} {...rest} className={`${CONTROL} ${className}`}>
      {children}
    </select>
  );
}
